/**
 * What a `read_document` call adds under its row: one quiet line when the
 * model read a filed rendition or the turn spent its page budget. The row
 * title already names the pages read, so there is nothing else to show.
 */
import type { FC } from "react";

export type ReadDocumentOutput = {
  form?: "pdf" | "image" | "rendition" | "text" | string;
  /** `page_budget.remaining` on the wire: native pages the turn has left. */
  budget_remaining?: number | null;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/** The parts of a `read_document` result the note renders, or null. */
export const readDocumentOutputOf = (
  output: unknown
): ReadDocumentOutput | null => {
  if (!isRecord(output) || output.error) return null;
  return {
    form: typeof output.form === "string" ? output.form : undefined,
    budget_remaining:
      isRecord(output.page_budget) &&
      typeof output.page_budget.remaining === "number"
        ? output.page_budget.remaining
        : null
  };
};

/** The note's words, or null when there is nothing to say. */
export const readNoteText = (out: ReadDocumentOutput): string | null => {
  const parts = [
    out.form === "rendition" ? "read as filed" : null,
    typeof out.budget_remaining === "number" && out.budget_remaining <= 0
      ? "page budget reached"
      : null
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
};

/** List reads whose empty result says so in words: `[list key, note]`. */
const EMPTY_LISTS: Readonly<Record<string, readonly [string, string]>> = {
  list_client_cases: ["cases", "No cases yet"]
};

/** The note for a list read that came back empty, or null. */
export const emptyListNote = (name: string, output: unknown): string | null => {
  const empty = EMPTY_LISTS[name];
  if (!empty || !isRecord(output) || output.error) return null;
  const list = output[empty[0]];
  return Array.isArray(list) && list.length === 0 ? empty[1] : null;
};

export const ReadNote: FC<{ text: string }> = ({ text }) => (
  <div className="text-xs text-fg-3" data-testid="read-note">
    {text}
  </div>
);
